import { useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Tags, Upload, Store, Sparkles, Workflow, IndianRupee, Settings, Users, LogOut, Menu, ExternalLink, Send, KeyRound, Rocket, Users2, BarChart3, Palette, Megaphone } from "lucide-react";
import { useAuth } from "@/components/admin/auth";
import { Logo } from "@/components/site/Header";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

const NAV = [
  ["/admin", "Overview", LayoutDashboard, true],
  ["/admin/deals", "Deals", Tags],
  ["/admin/import", "Import & Export", Upload],
  ["/admin/catalog", "Catalog", Store],
  ["/admin/content", "AI Content", Sparkles],
  ["/admin/automation", "Automation", Workflow],
  ["/admin/social", "Social", Send],
  ["/admin/social-manager", "Social Media Manager", Megaphone],
  ["/admin/earnings", "Clicks & Earnings", IndianRupee],
  ["/admin/settings", "Settings & Budget", Settings],
  ["/admin/branding", "Site branding", Palette, false, "admin"],
  ["/admin/integrations", "Integrations & Keys", KeyRound, false, "admin"],
  ["/admin/launch", "Launch checklist", Rocket, false, "admin"],
  ["/admin/customers", "Customers", Users2, false, "admin"],
  ["/admin/customer-analytics", "Customer analytics", BarChart3, false, "admin"],
  ["/admin/team", "Team & Audit", Users, false, "admin"],
];

const SideNav = ({ user, onNav }) => (
  <nav className="flex-1 space-y-1 px-3" aria-label="Admin">
    {NAV.filter((n) => !n[4] || n[4] === user.role).map(([to, label, Icon, end]) => (
      <NavLink key={to} to={to} end={end} onClick={onNav} data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${isActive ? "bg-emerald-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}>
        <Icon size={18} /> {label}
      </NavLink>
    ))}
  </nav>
);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  if (user === undefined) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  const side = (
    <div className="flex flex-col h-full py-5 gap-6">
      <div className="px-5"><Logo light size="sidebar" /></div>
      <SideNav user={user} onNav={() => setOpen(false)} />
      <div className="px-5 space-y-3 text-sm">
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-slate-400 hover:text-white"><ExternalLink size={14} /> View website</a>
        <div className="text-slate-400 truncate" data-testid="admin-user-email">{user.email} · <span className="uppercase text-xs text-amber-400">{user.role}</span></div>
        <button onClick={async () => { await logout(); nav("/admin/login"); }} data-testid="admin-logout-button" className="flex items-center gap-2 text-slate-300 hover:text-white"><LogOut size={16} /> Sign out</button>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="admin-layout">
      <aside className="hidden lg:block w-64 bg-slate-900 text-slate-100 border-r border-slate-800 sticky top-0 h-screen">{side}</aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="bg-slate-900 border-slate-800 p-0 w-64"><SheetTitle className="sr-only">Admin menu</SheetTitle>{side}</SheetContent>
      </Sheet>
      <main className="flex-1 min-w-0">
        <div className="lg:hidden flex items-center gap-3 p-4 bg-slate-900 text-white">
          <button onClick={() => setOpen(true)} aria-label="Open admin menu" data-testid="admin-mobile-menu-button"><Menu /></button>
          <span className="font-display font-bold">Admin</span>
        </div>
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]"><Outlet /></div>
      </main>
    </div>
  );
}

export const PageTitle = ({ title, sub, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
    <div>
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="admin-page-title">{title}</h1>
      {sub && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{sub}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Stat = ({ label, value, hint, testid, tone = "slate" }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`font-display font-extrabold text-3xl mt-1 ${tone === "green" ? "text-emerald-700" : tone === "red" ? "text-red-600" : tone === "amber" ? "text-amber-600" : "text-slate-900"}`}>{value}</p>
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

export const Panel = ({ title, children, action, testid }) => (
  <section className="bg-white border border-slate-200 rounded-xl" data-testid={testid}>
    {title && <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100"><h2 className="font-bold">{title}</h2>{action}</div>}
    <div className="p-5">{children}</div>
  </section>
);

export const StatusBadge = ({ s, label }) => {
  const c = { published: "bg-emerald-100 text-emerald-800", pending_review: "bg-amber-100 text-amber-800", scheduled: "bg-sky-100 text-sky-800",
    rejected: "bg-slate-200 text-slate-700", expired: "bg-red-100 text-red-700", success: "bg-emerald-100 text-emerald-800", failed: "bg-red-100 text-red-700",
    partial: "bg-amber-100 text-amber-800", draft: "bg-sky-100 text-sky-800", approved: "bg-emerald-100 text-emerald-800", pending: "bg-amber-100 text-amber-800",
    paid: "bg-emerald-200 text-emerald-900", reversed: "bg-red-100 text-red-700", active: "bg-emerald-100 text-emerald-800",
    ready: "bg-violet-100 text-violet-800", due: "bg-orange-100 text-orange-800", cancelled: "bg-slate-200 text-slate-600" }[s] || "bg-slate-100 text-slate-700";
  return <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${c}`}>{label || (s || "—").replace("_", " ")}</span>;
};
